import { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Card, Table, Spinner, Badge, Modal, Button, Form } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { getMyWallets, getMyTransactions, lookupUser, sendP2POTP, verifyAndP2PTransfer, getMyP2PTransfers, createPaymentRequest, getPaymentStatus, getPaymentHistory } from '../../api/walletApi';
import { fmtIST } from '../../utils/dateFormat';
import { useAuth } from '../../context/AuthContext';

const cyan = '#00e5ff';

const inputStyle = {
  width: '100%',
  background: 'rgba(0,229,255,0.04)',
  border: '1px solid rgba(0,229,255,0.2)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const Wallet = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [searchParams] = useSearchParams();

  const type = searchParams.get('type');
  const walletKey   = type === 'earning' ? 'earning_wallet' : type === 'one' ? 'one_wallet' : 'main_wallet';
  const walletLabel = type === 'earning' ? 'Earning Wallet'  : type === 'one' ? 'One Wallet'  : 'Main Wallet';
  const walletType  = type || 'main';
  const isMain    = !type;
  const isEarning = type === 'earning';
  const isOne     = type === 'one';

  // Wallet data
  const [wallets, setWallets] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add Funds modal — auto-open when ?addFunds=1 is in the URL
  const [showAddFunds, setShowAddFunds] = useState(searchParams.get('addFunds') === '1');
  const [addAmount, setAddAmount] = useState('');
  const [addFundsStep, setAddFundsStep] = useState('amount'); // 'amount' | 'payment'
  const [addFundsLoading, setAddFundsLoading] = useState(false);
  const [addFundsError, setAddFundsError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatusData, setPaymentStatusData] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [copied, setCopied] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);
  const pollRef = useRef(null);

  // P2P Transfer modal
  const [showP2P, setShowP2P] = useState(false);
  const [p2pCode, setP2pCode] = useState('');
  const [p2pRecipient, setP2pRecipient] = useState(null);
  const [p2pVerifying, setP2pVerifying] = useState(false);
  const [p2pLookupError, setP2pLookupError] = useState('');
  const [p2pAmount, setP2pAmount] = useState('');
  const [p2pNote, setP2pNote] = useState('');
  const [p2pSubmitting, setP2pSubmitting] = useState(false);
  const [p2pError, setP2pError] = useState('');
  const [p2pSuccess, setP2pSuccess] = useState(false);

  // P2P OTP verification
  const [p2pOTPStep, setP2pOTPStep] = useState('details'); // 'details' or 'otp'
  const [p2pOTP, setP2pOTP] = useState('');
  const [p2pOTPTimer, setP2pOTPTimer] = useState(0);
  const [p2pOTPSending, setP2pOTPSending] = useState(false);
  const [p2pOTPError, setP2pOTPError] = useState('');
  const [p2pCanResendOTP, setP2pCanResendOTP] = useState(false);

  // Transfer history
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);

  const wallet = wallets?.[walletKey];
  const p2pNumAmount = parseFloat(p2pAmount) || 0;
  const p2pAmountExceeds = p2pNumAmount > 0 && wallet?.balance !== undefined && p2pNumAmount > wallet.balance;

  const fetchTransfers = () => {
    setLoadingTransfers(true);
    getMyP2PTransfers()
      .then((res) => setTransfers(res.data.transfers))
      .catch(() => {})
      .finally(() => setLoadingTransfers(false));
  };

  useEffect(() => {
    setLoading(true);
    const calls = [getMyWallets(), getMyTransactions(walletType)];
    if (isMain) calls.push(getPaymentHistory());
    Promise.all(calls)
      .then(([walletsRes, txnRes, depositRes]) => {
        setWallets(walletsRes.data.wallets);
        setTransactions(txnRes.data.transactions);
        if (depositRes) setDepositHistory(depositRes.data.payments || []);
      })
      .finally(() => setLoading(false));

    if (isOne) fetchTransfers();
  }, [walletType]); // eslint-disable-line

  // P2P OTP timer
  useEffect(() => {
    if (p2pOTPTimer > 0) {
      const timer = setTimeout(() => setP2pOTPTimer(p2pOTPTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (p2pOTPTimer === 0 && p2pOTPStep === 'otp') setP2pCanResendOTP(true);
  }, [p2pOTPTimer, p2pOTPStep]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const resetAddFunds = useCallback(() => {
    stopPolling();
    setAddAmount('');
    setAddFundsStep('amount');
    setAddFundsLoading(false);
    setAddFundsError('');
    setPaymentData(null);
    setPaymentStatusData(null);
    setCheckingStatus(false);
    setCopied(false);
  }, [stopPolling]);

  const handleCheckStatus = useCallback(async (paymentId) => {
    if (!paymentId) return;
    setCheckingStatus(true);
    try {
      const res = await getPaymentStatus(paymentId);
      setPaymentStatusData(res.data);
      if (res.data.status === 'confirmed') {
        stopPolling();
        const walletsRes = await getMyWallets();
        setWallets(walletsRes.data.wallets);
        getPaymentHistory().then((r) => setDepositHistory(r.data.payments || []));
      }
    } catch {
      // silently ignore polling errors
    } finally {
      setCheckingStatus(false);
    }
  }, [stopPolling]); // eslint-disable-line

  // Auto-poll every 6s while payment screen is open
  useEffect(() => {
    if (addFundsStep === 'payment' && paymentData?.paymentId) {
      pollRef.current = setInterval(() => handleCheckStatus(paymentData.paymentId), 6000);
    }
    return () => stopPolling();
  }, [addFundsStep, paymentData?.paymentId]); // eslint-disable-line

  const handleAddFundsProceed = async () => {
    const amt = Number(addAmount);
    if (!amt || amt <= 0) return;
    setAddFundsLoading(true);
    setAddFundsError('');
    try {
      const res = await createPaymentRequest({ amount: amt });
      setPaymentData(res.data);
      setAddFundsStep('payment');
    } catch (err) {
      setAddFundsError(err.response?.data?.message || 'Failed to create payment. Please try again.');
    } finally {
      setAddFundsLoading(false);
    }
  };

  const resetP2P = () => {
    setP2pCode('');
    setP2pRecipient(null);
    setP2pVerifying(false);
    setP2pLookupError('');
    setP2pAmount('');
    setP2pNote('');
    setP2pError('');
    setP2pSuccess(false);
    setP2pOTPStep('details');
    setP2pOTP('');
    setP2pOTPTimer(0);
    setP2pOTPError('');
    setP2pCanResendOTP(false);
  };

  const handleVerify = async () => {
    if (!p2pCode.trim()) return;
    setP2pVerifying(true);
    setP2pLookupError('');
    setP2pRecipient(null);
    try {
      const res = await lookupUser(p2pCode.trim());
      setP2pRecipient(res.data.user);
    } catch (err) {
      setP2pLookupError(err.response?.data?.message || 'User not found');
    } finally {
      setP2pVerifying(false);
    }
  };

  const handleSendP2POTP = async () => {
    if (!p2pRecipient || !p2pNumAmount || p2pAmountExceeds) return;
    setP2pOTPSending(true);
    setP2pOTPError('');
    try {
      await sendP2POTP();
      setP2pOTPStep('otp');
      setP2pOTPTimer(30);
      setP2pCanResendOTP(false);
    } catch (err) {
      setP2pOTPError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setP2pOTPSending(false);
    }
  };

  const handleResendP2POTP = async () => {
    setP2pOTPSending(true);
    setP2pOTPError('');
    try {
      await sendP2POTP();
      setP2pOTPTimer(30);
      setP2pCanResendOTP(false);
    } catch (err) {
      setP2pOTPError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setP2pOTPSending(false);
    }
  };

  const handleP2PSubmit = async () => {
    if (!p2pRecipient || !p2pNumAmount || p2pAmountExceeds || !p2pOTP.trim()) return;
    setP2pSubmitting(true);
    setP2pError('');
    setP2pOTPError('');
    try {
      await verifyAndP2PTransfer({
        recipientCode: p2pRecipient.userCode,
        amount: p2pNumAmount,
        note: p2pNote.trim(),
        otp: p2pOTP,
      });
      setP2pSuccess(true);
      const walletsRes = await getMyWallets();
      setWallets(walletsRes.data.wallets);
      fetchTransfers();
      setTimeout(() => { setShowP2P(false); resetP2P(); }, 2500);
    } catch (err) {
      setP2pOTPError(err.response?.data?.message || 'Transfer failed.');
    } finally {
      setP2pSubmitting(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>{walletLabel}</h5>

        {/* Balance card */}
        <Card className="p-4 mb-4" style={{
          background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.08) 0%, #0d0d0d 65%)',
          border: '1px solid rgba(0,229,255,0.2)',
          maxWidth: 460,
        }}>
          <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>USDT Balance</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: 36, fontWeight: 800, color: cyan, textShadow: '0 0 18px rgba(0,229,255,0.35)' }}>
                {Number(wallet?.balance ?? 0).toFixed(2)}
              </span>
              <span style={{ color: '#aaa', fontSize: 14, marginLeft: 8 }}>USDT</span>
            </div>
            {isMain && (
              <button
                onClick={() => { resetAddFunds(); setShowAddFunds(true); }}
                style={{ background: cyan, border: 'none', color: '#000', fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                + Add Funds
              </button>
            )}
            {isEarning && (
              <button
                onClick={() => navigate('/withdraw')}
                style={{ background: 'transparent', border: `1px solid ${cyan}`, color: cyan, fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Withdraw
              </button>
            )}
            {isOne && (
              <button
                onClick={() => { resetP2P(); setShowP2P(true); }}
                style={{ background: `linear-gradient(135deg, ${cyan} 0%, #00b8d9 100%)`, border: 'none', color: '#000', fontWeight: 700, fontSize: 13, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                ⇄ P2P Transfer
              </button>
            )}
          </div>
        </Card>

        {/* Transaction History */}
        <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Transaction History</div>
        <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>
          <Table responsive className="mb-0" style={{ background: 'transparent' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
                <th style={{ color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>Txn ID</th>
                <th style={{ color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>Date</th>
                <th style={{ color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>Type</th>
                <th style={{ color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>Amount</th>
                <th style={{ color: cyan, fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: '#555', textAlign: 'center', padding: '32px', border: 'none' }}>
                    No transactions yet
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ color: '#555', fontSize: 11, padding: '12px 20px', border: 'none', fontFamily: 'monospace' }}>
                      {txn._id.slice(-10).toUpperCase()}
                    </td>
                    <td style={{ color: '#888', fontSize: 12, padding: '12px 20px', border: 'none' }}>
                      {fmtIST(txn.createdAt)}
                    </td>
                    <td style={{ padding: '12px 20px', border: 'none' }}>
                      <Badge style={txn.txnType === 'credit'
                        ? { background: 'rgba(102,255,153,0.15)', color: '#66ff99', border: '1px solid #66ff9955', fontSize: 11, padding: '4px 10px' }
                        : { background: 'rgba(200,50,50,0.15)', color: '#ff6666', border: '1px solid rgba(200,50,50,0.3)', fontSize: 11, padding: '4px 10px' }
                      }>
                        {txn.txnType}
                      </Badge>
                    </td>
                    <td style={{ color: txn.txnType === 'credit' ? '#66ff99' : '#ff6666', fontWeight: 600, fontSize: 13, padding: '12px 20px', border: 'none' }}>
                      {txn.txnType === 'credit' ? '+' : '−'}{Number(txn.amount).toFixed(2)} USDT
                    </td>
                    <td style={{ color: '#aaa', fontSize: 12, padding: '12px 20px', border: 'none' }}>
                      {txn.comments || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Deposit History — Main Wallet only */}
        {isMain && depositHistory.length > 0 && (
          <div style={{ marginTop: 36 }}>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Deposit History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {depositHistory.map((d) => {
                const statusColor = d.status === 'confirmed' ? '#66ff99' : d.status === 'failed' ? '#ff6666' : '#ffaa44';
                const statusBg = d.status === 'confirmed' ? 'rgba(102,255,153,0.08)' : d.status === 'failed' ? 'rgba(200,50,50,0.08)' : 'rgba(255,170,0,0.08)';
                const statusBorder = d.status === 'confirmed' ? 'rgba(102,255,153,0.2)' : d.status === 'failed' ? 'rgba(200,50,50,0.2)' : 'rgba(255,170,0,0.2)';
                return (
                  <div key={d._id} style={{ background: '#0d0d0d', border: '1px solid rgba(0,229,255,0.08)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 15 }}>{d.amount} USDT</div>
                      <div style={{ color: '#555', fontSize: 11, marginTop: 3 }}>USDT · BSC (BEP20) · {fmtIST(d.createdAt)}</div>
                      {d.transactionHash && (
                        <div style={{ color: '#444', fontSize: 10, fontFamily: 'monospace', marginTop: 2 }}>TX: {d.transactionHash.slice(0, 20)}...</div>
                      )}
                    </div>
                    <span style={{ background: statusBg, border: `1px solid ${statusBorder}`, color: statusColor, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                      {d.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* P2P Transfer History — OneWallet only */}
        {isOne && (
          <div style={{ marginTop: 36 }}>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>P2P Transfer History</div>
            {loadingTransfers ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" style={{ color: cyan }} />
              </div>
            ) : transfers.length === 0 ? (
              <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                No transfers yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {transfers.map((t) => {
                  const isSent = t.senderId._id === authUser?._id;
                  const other  = isSent ? t.recipientId : t.senderId;
                  return (
                    <div key={t._id} style={{
                      background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.04) 0%, #0d0d0d 65%)',
                      border: '1px solid rgba(0,229,255,0.1)',
                      borderRadius: 12,
                      padding: '14px 18px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                        {/* Avatar */}
                        <div style={{
                          width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                          background: isSent ? 'rgba(255,102,102,0.12)' : 'rgba(102,255,153,0.12)',
                          border: `2px solid ${isSent ? 'rgba(255,102,102,0.3)' : 'rgba(102,255,153,0.3)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 16, fontWeight: 800,
                          color: isSent ? '#ff6666' : '#66ff99',
                        }}>
                          {other.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                            {isSent ? 'To' : 'From'}: {other.name}
                          </div>
                          <div style={{ color: '#555', fontSize: 11, marginTop: 1 }}>Code: {other.userCode}</div>
                          {t.note && (
                            <div style={{ color: '#666', fontSize: 11, fontStyle: 'italic', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              "{t.note}"
                            </div>
                          )}
                          <div style={{ color: '#444', fontSize: 11, marginTop: 4 }}>
                            {fmtIST(t.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                        <div style={{ color: isSent ? '#ff6666' : '#66ff99', fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>
                          {isSent ? '−' : '+'}{Number(t.amount).toFixed(2)} USDT
                        </div>
                        <span style={{
                          background: isSent ? 'rgba(255,102,102,0.12)' : 'rgba(102,255,153,0.12)',
                          border: `1px solid ${isSent ? 'rgba(255,102,102,0.3)' : 'rgba(102,255,153,0.3)'}`,
                          color: isSent ? '#ff6666' : '#66ff99',
                          borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                        }}>
                          {isSent ? 'SENT' : 'RECEIVED'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Container>

      {/* ── Add Funds Modal ── */}
      <Modal show={showAddFunds} onHide={() => { setShowAddFunds(false); resetAddFunds(); }} centered>
        <Modal.Header closeButton style={{ background: '#0d0d0d', border: '1px solid rgba(0,229,255,0.15)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
            {addFundsStep === 'payment' ? 'Deposit USDT' : 'Add Funds'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#0d0d0d', borderLeft: '1px solid rgba(0,229,255,0.15)', borderRight: '1px solid rgba(0,229,255,0.15)', padding: '24px' }}>

          {addFundsStep === 'amount' ? (
            <>
              <Form.Group>
                <Form.Label style={{ color: '#888', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Amount (USDT)
                </Form.Label>
                <Form.Control
                  type="number" min="10" placeholder="Min. $10 USDT"
                  value={addAmount}
                  onChange={(e) => { setAddAmount(e.target.value); setAddFundsError(''); }}
                  style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', color: '#fff', borderRadius: 8, padding: '10px 14px' }}
                />
                <div style={{ color: '#555', fontSize: 11, marginTop: 6 }}>Minimum deposit: $10 USDT</div>
              </Form.Group>
              {addFundsError && (
                <div style={{ marginTop: 14, background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#ff6666', fontSize: 13 }}>
                  {addFundsError}
                </div>
              )}
            </>
          ) : (
            <div>
              {/* Chain badge */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                <span style={{ background: 'rgba(243,186,47,0.12)', border: '1px solid rgba(243,186,47,0.35)', color: '#f3ba2f', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em' }}>
                  USDT (BEP20) · BSC Network
                </span>
              </div>

              {/* Amount */}
              <div style={{ textAlign: 'center', marginBottom: 18 }}>
                <div style={{ color: '#555', fontSize: 11, marginBottom: 2 }}>Send exactly</div>
                <div style={{ color: '#66ff99', fontWeight: 800, fontSize: 28 }}>{paymentData?.receiveAmount || paymentData?.amount} <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>USDT</span></div>
              </div>

              {/* QR Code */}
              {paymentData?.paymentAddress && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                  <div style={{ background: '#fff', borderRadius: 12, padding: 12 }}>
                    <QRCodeSVG value={paymentData.paymentAddress} size={160} />
                  </div>
                </div>
              )}

              {/* Address + copy */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#888', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Deposit Address</div>
                <div style={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#fff', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, lineHeight: 1.5 }}>
                    {paymentData?.paymentAddress}
                  </span>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(paymentData.paymentAddress); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    style={{ background: copied ? 'rgba(102,255,153,0.15)' : 'rgba(0,229,255,0.1)', border: `1px solid ${copied ? 'rgba(102,255,153,0.4)' : 'rgba(0,229,255,0.3)'}`, color: copied ? '#66ff99' : cyan, borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Warning */}
              <div style={{ background: 'rgba(255,170,0,0.07)', border: '1px solid rgba(255,170,0,0.2)', borderRadius: 8, padding: '10px 14px', color: '#ffaa44', fontSize: 12, marginBottom: 18, lineHeight: 1.6 }}>
                ⚠️ Only send <strong>USDT on BSC (BEP20)</strong>. Sending on the wrong network will result in permanent loss of funds.
              </div>

              {/* Payment status */}
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: paymentStatusData ? 8 : 0 }}>
                  <span style={{ color: '#888', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payment Status</span>
                  {checkingStatus && <span style={{ color: '#555', fontSize: 11 }}>Checking...</span>}
                  {!checkingStatus && !paymentStatusData && <span style={{ color: '#555', fontSize: 11 }}>Waiting for payment...</span>}
                </div>
                {paymentStatusData && (
                  <div style={{ color: paymentStatusData.status === 'confirmed' ? '#66ff99' : paymentStatusData.paymentStatus === 'confirming' ? '#66aaff' : '#ffaa44', fontWeight: 600, fontSize: 13 }}>
                    {paymentStatusData.status === 'confirmed' && '✓ Confirmed — wallet has been credited!'}
                    {paymentStatusData.status === 'pending' && paymentStatusData.paymentStatus === 'confirming' && '⏳ Transaction found, waiting for confirmations...'}
                    {paymentStatusData.status === 'pending' && paymentStatusData.paymentStatus === 'waiting' && '⏳ Waiting for your payment...'}
                    {paymentStatusData.status === 'pending' && !['confirming','waiting'].includes(paymentStatusData.paymentStatus) && `⏳ ${paymentStatusData.paymentStatus || 'Pending'}...`}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ background: '#0d0d0d', border: '1px solid rgba(0,229,255,0.15)', gap: 10 }}>
          <Button
            onClick={() => { setShowAddFunds(false); resetAddFunds(); }}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#aaa', fontWeight: 600, flex: 1 }}
          >
            {addFundsStep === 'payment' ? 'Close' : 'Cancel'}
          </Button>
          {addFundsStep === 'amount' ? (
            <Button
              onClick={handleAddFundsProceed}
              disabled={!addAmount || Number(addAmount) < 10 || addFundsLoading}
              style={{ background: (!addAmount || Number(addAmount) < 10 || addFundsLoading) ? 'rgba(255,255,255,0.06)' : cyan, border: 'none', color: (!addAmount || Number(addAmount) < 10 || addFundsLoading) ? '#555' : '#000', fontWeight: 700, flex: 1 }}
            >
              {addFundsLoading ? 'Generating...' : 'Get Deposit Address'}
            </Button>
          ) : (
            <Button
              onClick={() => handleCheckStatus(paymentData?.paymentId)}
              disabled={checkingStatus || paymentStatusData?.status === 'confirmed'}
              style={{ background: paymentStatusData?.status === 'confirmed' ? 'rgba(102,255,153,0.1)' : 'rgba(0,229,255,0.08)', border: `1px solid ${paymentStatusData?.status === 'confirmed' ? 'rgba(102,255,153,0.3)' : 'rgba(0,229,255,0.25)'}`, color: paymentStatusData?.status === 'confirmed' ? '#66ff99' : cyan, fontWeight: 700, flex: 1 }}
            >
              {checkingStatus ? 'Checking...' : paymentStatusData?.status === 'confirmed' ? '✓ Confirmed' : 'Check Status'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── P2P Transfer Modal ── */}
      <Modal show={showP2P} onHide={() => { setShowP2P(false); resetP2P(); }} centered>
        <Modal.Header closeButton style={{ background: '#0d0d0d', border: '1px solid rgba(0,229,255,0.15)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>⇄ P2P Transfer</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: '#0d0d0d', borderLeft: '1px solid rgba(0,229,255,0.15)', borderRight: '1px solid rgba(0,229,255,0.15)', padding: '24px' }}>
          {p2pSuccess ? (
            /* Success state */
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 54, marginBottom: 16 }}>✅</div>
              <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Transfer Successful!</div>
              <div style={{ color: '#aaa', fontSize: 13 }}>
                {p2pNumAmount.toFixed(2)} USDT sent to{' '}
                <span style={{ color: '#fff', fontWeight: 600 }}>{p2pRecipient?.name}</span>
              </div>
            </div>
          ) : (
            <>
              {/* Step 1 — Recipient lookup */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Recipient User Code</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Enter user code..."
                    value={p2pCode}
                    onChange={(e) => { setP2pCode(e.target.value); setP2pLookupError(''); setP2pRecipient(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                    style={{ ...inputStyle, flex: 1 }}
                    disabled={p2pVerifying || p2pSubmitting}
                  />
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!p2pCode.trim() || p2pVerifying || p2pSubmitting}
                    style={{
                      background: p2pRecipient
                        ? 'rgba(102,255,153,0.15)'
                        : !p2pCode.trim() || p2pVerifying ? 'rgba(0,229,255,0.25)' : cyan,
                      border: p2pRecipient ? '1px solid rgba(102,255,153,0.4)' : 'none',
                      color: p2pRecipient ? '#66ff99' : '#000',
                      fontWeight: 700, fontSize: 13, borderRadius: 8,
                      padding: '0 16px', minWidth: 84,
                      cursor: !p2pCode.trim() || p2pVerifying || p2pSubmitting ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p2pVerifying ? '...' : p2pRecipient ? '✓ Verified' : 'Verify'}
                  </button>
                </div>
                {p2pLookupError && (
                  <div style={{ color: '#ff6666', fontSize: 12, marginTop: 6 }}>{p2pLookupError}</div>
                )}
              </div>

              {/* Verified recipient card */}
              {p2pRecipient && (
                <div style={{
                  background: 'rgba(102,255,153,0.06)',
                  border: '1px solid rgba(102,255,153,0.25)',
                  borderRadius: 10, padding: '14px 16px', marginBottom: 24,
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(102,255,153,0.15)',
                    border: '2px solid rgba(102,255,153,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, fontWeight: 800, color: '#66ff99',
                  }}>
                    {p2pRecipient.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{p2pRecipient.name}</div>
                    <div style={{ color: '#66ff99', fontSize: 12, marginTop: 2 }}>Code: {p2pRecipient.userCode}</div>
                  </div>
                  <div style={{ background: 'rgba(102,255,153,0.18)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#66ff99', fontWeight: 700, flexShrink: 0 }}>
                    ✓ Verified
                  </div>
                </div>
              )}

              {/* Step 2 — Amount + Note (shown after verification) */}
              {p2pRecipient && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Amount (USDT)</label>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      placeholder="0.00"
                      value={p2pAmount}
                      onChange={(e) => { setP2pAmount(e.target.value); setP2pError(''); }}
                      style={{ ...inputStyle, fontSize: 22, fontWeight: 700, borderColor: p2pAmountExceeds ? 'rgba(200,50,50,0.5)' : 'rgba(0,229,255,0.2)' }}
                      disabled={p2pSubmitting}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ color: '#555', fontSize: 11 }}>Available: {Number(wallet?.balance ?? 0).toFixed(2)} USDT</span>
                      {p2pAmountExceeds && <span style={{ color: '#ff6666', fontSize: 11 }}>Exceeds balance</span>}
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Note (Optional)</label>
                    <textarea
                      rows={2}
                      placeholder="Add a note to this transfer..."
                      value={p2pNote}
                      onChange={(e) => setP2pNote(e.target.value)}
                      style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit' }}
                      disabled={p2pSubmitting}
                    />
                  </div>

                  {/* Transfer summary */}
                  {p2pNumAmount > 0 && !p2pAmountExceeds && (
                    <div style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.12)', borderRadius: 8, padding: '12px 16px', marginBottom: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ color: '#888', fontSize: 12 }}>You send</span>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{p2pNumAmount.toFixed(2)} USDT</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#888', fontSize: 12 }}>{p2pRecipient.name} receives</span>
                        <span style={{ color: '#66ff99', fontWeight: 700, fontSize: 12 }}>{p2pNumAmount.toFixed(2)} USDT</span>
                      </div>
                      <div style={{ color: '#444', fontSize: 10, textAlign: 'center', marginTop: 8 }}>
                        No service charge on P2P transfers
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* OTP Verification Step */}
              {p2pOTPStep === 'otp' && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(0,229,255,0.1)' }}>
                  <label style={labelStyle}>Enter OTP</label>
                  <p style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>6-digit OTP sent to your email</p>
                  <input
                    type="text"
                    value={p2pOTP}
                    onChange={(e) => { setP2pOTP(e.target.value.replace(/\D/g, '').slice(0, 6)); setP2pOTPError(''); }}
                    placeholder="000000"
                    maxLength="6"
                    style={{ ...inputStyle, fontSize: 22, fontWeight: 700, letterSpacing: 8, textAlign: 'center' }}
                  />
                  {p2pOTPTimer > 0 && (
                    <div style={{ color: '#888', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                      Resend available in {p2pOTPTimer}s
                    </div>
                  )}
                </div>
              )}

              {p2pOTPError && (
                <div style={{ marginTop: 14, background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#ff6666', fontSize: 13 }}>
                  {p2pOTPError}
                </div>
              )}

              {p2pError && (
                <div style={{ marginTop: 14, background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 8, padding: '10px 14px', color: '#ff6666', fontSize: 13 }}>
                  {p2pError}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        {!p2pSuccess && (
          <Modal.Footer style={{ background: '#0d0d0d', border: '1px solid rgba(0,229,255,0.15)', display: 'flex', gap: 10 }}>
            <Button
              onClick={() => { setShowP2P(false); resetP2P(); }}
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#aaa', fontWeight: 600, flex: 1 }}
            >
              {p2pOTPStep === 'otp' ? '← Back' : 'Cancel'}
            </Button>

            {p2pOTPStep === 'details' ? (
              <Button
                onClick={handleSendP2POTP}
                disabled={!p2pRecipient || !p2pNumAmount || p2pAmountExceeds || p2pOTPSending}
                style={{
                  background: (!p2pRecipient || !p2pNumAmount || p2pAmountExceeds || p2pOTPSending)
                    ? 'rgba(255,255,255,0.05)'
                    : `linear-gradient(135deg, ${cyan} 0%, #00b8d9 100%)`,
                  border: 'none',
                  color: (!p2pRecipient || !p2pNumAmount || p2pAmountExceeds || p2pOTPSending) ? '#555' : '#000',
                  fontWeight: 700, fontSize: 14, padding: '8px 28px', flex: 1,
                }}
              >
                {p2pOTPSending ? 'Sending OTP...' : 'Request OTP →'}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleResendP2POTP}
                  disabled={!p2pCanResendOTP || p2pOTPSending}
                  style={{
                    background: p2pCanResendOTP ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(0,229,255,0.2)',
                    color: p2pCanResendOTP ? cyan : '#444',
                    fontWeight: 600, fontSize: 13, padding: '6px 14px', flex: 1,
                  }}
                >
                  Resend OTP
                </Button>
                <Button
                  onClick={handleP2PSubmit}
                  disabled={!p2pOTP || p2pOTP.length !== 6 || p2pSubmitting}
                  style={{
                    background: (!p2pOTP || p2pOTP.length !== 6 || p2pSubmitting)
                      ? 'rgba(255,255,255,0.05)'
                      : `linear-gradient(135deg, ${cyan} 0%, #00b8d9 100%)`,
                    border: 'none',
                    color: (!p2pOTP || p2pOTP.length !== 6 || p2pSubmitting) ? '#555' : '#000',
                    fontWeight: 700, fontSize: 14, padding: '8px 28px', flex: 1,
                  }}
                >
                  {p2pSubmitting ? 'Sending...' : 'Send Transfer →'}
                </Button>
              </>
            )}
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default Wallet;
